import React, { createContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = async () => {
    await supabase.auth.signOut().catch(() => {});
    setSession(null);
    setProfile(null);
  };

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('agency_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Erro ao buscar perfil:', error.message);
        // Sessao invalida ou perfil nao existe — limpa tudo
        await clearSession();
        return null;
      }
      return data;
    } catch (err) {
      console.error('Erro inesperado ao buscar perfil:', err);
      await clearSession();
      return null;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: s }, error }) => {
      if (error || !s) {
        // Sessao corrompida ou expirada — limpa automaticamente
        if (error) await clearSession();
        setLoading(false);
        return;
      }
      setSession(s);
      if (s.user) {
        const p = await fetchProfile(s.user.id);
        setProfile(p);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, s) => {
        if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' && !s) {
          setSession(null);
          setProfile(null);
          return;
        }
        setSession(s);
        if (s?.user) {
          const p = await fetchProfile(s.user.id);
          setProfile(p);
        } else {
          setProfile(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    await supabase.auth.signOut().catch(() => {});
    setSession(null);
    setProfile(null);
  };

  const value = {
    session,
    user: session?.user ?? null,
    profile,
    loading,
    signIn,
    signOut,
    refetchProfile: async () => {
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
